/**
 * `@dxf-ai-editor/cad-core` — typed per-kind entity grips (DEFUSE S10b).
 *
 * AutoCAD-parity grip sets computed directly from the canonical
 * `DrawingEntity` fields. Reference sources: Autodesk DXF reference (grip
 * behaviour per entity type), `apps/web/src/app/scene/gripCalculation.ts`
 * (the legacy string-bag reference for the "base" per-entity grip set —
 * ported here field-for-field) and `apps/web/src/app/scene3d/grips/
 * computeGrips.ts` (the GripKind taxonomy this module's `GripKind` mirrors).
 *
 * Scope note (Verification report has the full rationale): a `Block`
 * (INSERT) entity's grip set here is the single insertion-point grip only —
 * EXACTLY `gripCalculation.ts`'s `calculateGrips` base case for "Block".
 * AutoCAD's ADDITIONAL dynamic-block / MINSERT-grid parameter grips
 * (visibility chevrons, lookup chevrons, flip arrows, per-axis stretch
 * handles) are a large, separate, already-implemented subsystem
 * (`blockGripCollector.ts` + `blockGripCollector.minsert.ts`, ~1100 lines)
 * layered ON TOP of the base set by a SEPARATE pass even in the legacy /
 * live code (`scene3d/grips/computeGrips.ts` calls `collectBlockGrips`
 * independently of `calculateGrips`) — porting that whole subsystem to the
 * typed model is out of scope for this foundation slice and tracked as
 * explicit follow-up work, not a silent gap.
 *
 * Model-space convention: NO Y-flip anywhere in this file (a Y-flip is a
 * render-only display transform, never baked into canonical WCS geometry —
 * see `common.ts`'s module doc).
 */

import {
  bulgeArc,
  bulgeArcPointAt,
  edgeToCurve,
  type OleFrameEntity,
  type TableEntity,
  type DrawingEntity,
  type LineEntity,
  type PointEntity,
  type CircleEntity,
  type ArcEntity,
  type EllipseEntity,
  type SplineEntity,
  type LwpolylineEntity,
  type TextEntity,
  type MtextEntity,
  type DimensionEntity,
  type MultileaderEntity,
  type RevisionCloudEntity,
  type WipeoutEntity,
  type HatchEntity,
  type BlockInsertEntity,
  type ArrayRectEntity,
  type ViewportEntity,
  type PolylineVertex,
} from "@dxf-ai-editor/drawing-model";

import { arrayAxisAngleDeg, midpoint, polarPoint, rotateByArrayAxisAngle, sweepDeg } from "./common";
import { multileaderDoglegEnd, multileaderNodeLastVertex } from "./leafBounds";
import type { GripPoint } from "./types";

/**
 * Spread-in an optional `z` value — with `exactOptionalPropertyTypes: true`
 * a `GripPoint` literal must OMIT `z` entirely rather than assign
 * `z: undefined`.
 */
function optZ(z: number | undefined): { z: number } | Record<string, never> {
  return z !== undefined ? { z } : {};
}

function lineGrips(e: LineEntity): GripPoint[] {
  const s = e.start;
  const en = e.end;
  return [
    { id: "start", kind: "endpoint", x: s.x, y: s.y, ...optZ(s.z), patch: { field: "start" } },
    {
      id: "midpoint",
      kind: "midpoint",
      x: (s.x + en.x) / 2,
      y: (s.y + en.y) / 2,
      // Dragging the midpoint translates BOTH endpoints — no single field.
    },
    { id: "end", kind: "endpoint", x: en.x, y: en.y, ...optZ(en.z), patch: { field: "end" } },
  ];
}

function pointGrips(e: PointEntity): GripPoint[] {
  const l = e.location;
  return [{ id: "location", kind: "insertion", x: l.x, y: l.y, ...optZ(l.z), patch: { field: "location" } }];
}

function circleGrips(e: CircleEntity): GripPoint[] {
  const { x: cx, y: cy } = e.center;
  const r = e.radius;
  return [
    { id: "center", kind: "center", x: cx, y: cy, patch: { field: "center" } },
    { id: "quad-right", kind: "quadrant", x: cx + r, y: cy, radiusMode: true, patch: { field: "radius" } },
    { id: "quad-top", kind: "quadrant", x: cx, y: cy + r, radiusMode: true, patch: { field: "radius" } },
    { id: "quad-left", kind: "quadrant", x: cx - r, y: cy, radiusMode: true, patch: { field: "radius" } },
    { id: "quad-bottom", kind: "quadrant", x: cx, y: cy - r, radiusMode: true, patch: { field: "radius" } },
  ];
}

function arcGrips(e: ArcEntity): GripPoint[] {
  const { x: cx, y: cy } = e.center;
  const r = e.radius;
  const sweep = sweepDeg(e.startAngle, e.endAngle);
  const mid = polarPoint(cx, cy, r, e.startAngle + sweep / 2);
  const start = polarPoint(cx, cy, r, e.startAngle);
  const end = polarPoint(cx, cy, r, e.endAngle);
  return [
    { id: "center", kind: "center", x: cx, y: cy, patch: { field: "center" } },
    { id: "start", kind: "endpoint", x: start.x, y: start.y, patch: { field: "startAngle" } },
    { id: "end", kind: "endpoint", x: end.x, y: end.y, patch: { field: "endAngle" } },
    { id: "arc-midpoint", kind: "midpoint", x: mid.x, y: mid.y, radiusMode: true, patch: { field: "radius" } },
  ];
}

function ellipseGrips(e: EllipseEntity): GripPoint[] {
  const { x: cx, y: cy } = e.center;
  // Direction stays XY-only — `cos`/`sin` must be a proper 2-D UNIT vector
  // (the render is a 2-D screen-space placement); see the drag-translator
  // (`apps/web/src/app/state/gripDragTranslateEllipse.ts`, rounds 5-7,
  // local Codex review) for the matching write-side reasoning.
  const majorMag2D = Math.hypot(e.majorAxisEndpoint.x, e.majorAxisEndpoint.y);
  const safeMag = majorMag2D > 0 ? majorMag2D : 1;
  const cos = e.majorAxisEndpoint.x / safeMag;
  const sin = e.majorAxisEndpoint.y / safeMag;
  // The minor-grip DISTANCE along that direction, by contrast, must use the
  // FULL 3-D major-axis length (round 8, local Codex review — the grip-drag
  // translator now persists Ratio relative to the 3-D length on a TILTED
  // ellipse; round-tripping the SAME ellipse through this renderer must
  // place the grip at the SAME distance the translator treats as "no
  // change", or dragging the visible handle back onto itself silently
  // shrinks/grows a tilted ellipse). `majorAxisEndpoint.z` defaults to 0 for
  // the common planar case, reducing to the pre-round-8 formula exactly.
  const majorMag3D = Math.hypot(
    e.majorAxisEndpoint.x,
    e.majorAxisEndpoint.y,
    e.majorAxisEndpoint.z ?? 0,
  );
  const minorMag = majorMag3D * e.ratio;
  return [
    { id: "center", kind: "center", x: cx, y: cy, patch: { field: "center" } },
    { id: "major-pos", kind: "major-axis", x: cx + majorMag2D * cos, y: cy + majorMag2D * sin, patch: { field: "majorAxisEndpoint" } },
    { id: "major-neg", kind: "major-axis", x: cx - majorMag2D * cos, y: cy - majorMag2D * sin, patch: { field: "majorAxisEndpoint" } },
    { id: "minor-pos", kind: "minor-axis", x: cx - minorMag * sin, y: cy + minorMag * cos, patch: { field: "ratio" } },
    { id: "minor-neg", kind: "minor-axis", x: cx + minorMag * sin, y: cy - minorMag * cos, patch: { field: "ratio" } },
  ];
}

function textGrips(e: TextEntity): GripPoint[] {
  const p = e.insertionPoint;
  return [{ id: "insertion", kind: "insertion", x: p.x, y: p.y, patch: { field: "insertionPoint" } }];
}

function mtextGrips(e: MtextEntity): GripPoint[] {
  const p = e.insertionPoint;
  return [{ id: "insertion", kind: "insertion", x: p.x, y: p.y, patch: { field: "insertionPoint" } }];
}

/**
 * Shared vertex + bulge-aware segment-midpoint grip builder for the two
 * LWPOLYLINE-family kinds (LWPOLYLINE itself, and Revision Cloud — stored
 * on disk as an LWPOLYLINE-derived entity per `model.ts`). One truth for
 * both callers below; reuses `bulgeArc`/`bulgeArcPointAt`
 * (`drawing-model/bulgeArc.ts`) for the arc-bump midpoint of a bulged
 * segment (falling back to the chord midpoint for a straight segment).
 *
 * The closing-segment guard is `closed && vertices.length >= 2` (not the
 * legacy `> 2`) — matching `viewportMembership.ts::lwpolylineBounds`'s
 * documented Codex #326 P2 fix: a 2-vertex closed polyline (a circle
 * authored as two bulged semicircles) has a real closing arc that must not
 * be dropped.
 */
function bulgePolylineGrips(
  vertices: readonly PolylineVertex[],
  closed: boolean,
): GripPoint[] {
  const grips: GripPoint[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const v = vertices[i]!;
    grips.push({ id: `vertex-${i}`, kind: "vertex", x: v.x, y: v.y, patch: { field: "vertices", index: i } });
  }
  const segMid = (i: number, j: number): void => {
    const v1 = vertices[i]!;
    const v2 = vertices[j]!;
    const arc = bulgeArc(v1, v2, v1.bulge);
    const mid = arc ? bulgeArcPointAt(arc, 0.5) : midpoint(v1, v2);
    grips.push({ id: `seg-mid-${i}`, kind: "segment-mid", x: mid.x, y: mid.y, patch: { field: "vertices", index: i } });
  };
  for (let i = 0; i < vertices.length - 1; i++) segMid(i, i + 1);
  if (closed && vertices.length >= 2) segMid(vertices.length - 1, 0);
  return grips;
}

function lwpolylineGrips(e: LwpolylineEntity): GripPoint[] {
  return bulgePolylineGrips(e.vertices, e.closed);
}

function revisionCloudGrips(e: RevisionCloudEntity): GripPoint[] {
  return bulgePolylineGrips(e.vertices, e.closed);
}

function splineGrips(e: SplineEntity): GripPoint[] {
  const grips: GripPoint[] = [];
  for (let i = 0; i < e.controlPoints.length; i++) {
    const cp = e.controlPoints[i]!;
    grips.push({ id: `cp-${i}`, kind: "control-point", x: cp.x, y: cp.y, z: cp.z, patch: { field: "controlPoints", index: i } });
  }
  for (let i = 0; i < e.fitPoints.length; i++) {
    const fp = e.fitPoints[i]!;
    grips.push({ id: `fit-${i}`, kind: "fit-point", x: fp.x, y: fp.y, z: fp.z, patch: { field: "fitPoints", index: i } });
  }
  return grips;
}

function wipeoutGrips(e: WipeoutEntity): GripPoint[] {
  return e.vertices.map((v, i) => ({
    id: `vertex-${i}`,
    kind: "vertex" as const,
    x: v.x,
    y: v.y,
    patch: { field: "vertices", index: i },
  }));
}

/**
 * DIMENSION grips — one per DEFINITION point that is actually present.
 * Unlike the legacy string-bag path (which always defaults an absent
 * extension point to (0,0) and emits a grip there), the typed
 * `extLine1Point`/`extLine2Point`/`thirdPoint`/`arcPoint` fields are
 * genuinely optional — a Radius/Diameter dimension has no extension
 * lines and correctly yields no ext-line grips.
 */
function dimensionGrips(e: DimensionEntity): GripPoint[] {
  const grips: GripPoint[] = [];
  if (e.extLine1Point) {
    grips.push({ id: "ext1", kind: "ext-line", x: e.extLine1Point.x, y: e.extLine1Point.y, patch: { field: "extLine1Point" } });
  }
  if (e.extLine2Point) {
    grips.push({ id: "ext2", kind: "ext-line", x: e.extLine2Point.x, y: e.extLine2Point.y, patch: { field: "extLine2Point" } });
  }
  grips.push({ id: "dimline", kind: "dim-line", x: e.defPoint.x, y: e.defPoint.y, patch: { field: "defPoint" } });
  if (e.thirdPoint) {
    grips.push({ id: "third-point", kind: "third-point", x: e.thirdPoint.x, y: e.thirdPoint.y, patch: { field: "thirdPoint" } });
  }
  if (e.arcPoint) {
    grips.push({ id: "arc-point", kind: "arc-point", x: e.arcPoint.x, y: e.arcPoint.y, patch: { field: "arcPoint" } });
  }
  return grips;
}

/**
 * MULTILEADER grips — arrow / intermediate-vertex / landing per leader
 * line, dogleg endpoint (when enabled, INCLUDING the horizontal fallback
 * when the node carries no explicit `doglegVector` — Codex #467 round-2 P2
 * "Emit the fallback multileader dogleg grip"; {@link multileaderDoglegEnd}
 * is the one-truth shared with `leafBounds.ts::multileaderBounds` and
 * `select.ts`'s Multileader case), plus the text/block content location.
 * The typed `leaderRoots` tree is already fully structured (no JSON-blob
 * decode) — this walk directly mirrors
 * `scene3d/grips/computeGrips.ts::computeMultileaderGrips`
 * (`gripCalculation.ts::computeDoglegEndpoint`'s fallback rule).
 */
function multileaderGrips(e: MultileaderEntity): GripPoint[] {
  const out: GripPoint[] = [];
  for (let ri = 0; ri < e.leaderRoots.length; ri++) {
    const root = e.leaderRoots[ri]!;
    for (let ni = 0; ni < root.nodes.length; ni++) {
      const node = root.nodes[ni]!;
      for (let li = 0; li < node.lines.length; li++) {
        const line = node.lines[li]!;
        const verts = line.vertices;
        if (verts.length === 0) continue;
        const first = verts[0]!;
        out.push({ id: `mleader-arrow-${ri}-${ni}-${li}`, kind: "mleader-arrow", x: first.x, y: first.y });
        for (let vi = 1; vi < verts.length - 1; vi++) {
          const v = verts[vi]!;
          out.push({ id: `mleader-vertex-${ri}-${ni}-${li}-${vi}`, kind: "mleader-vertex", x: v.x, y: v.y });
        }
        if (verts.length > 1) {
          const last = verts[verts.length - 1]!;
          out.push({ id: `mleader-landing-${ri}-${ni}-${li}`, kind: "mleader-landing", x: last.x, y: last.y });
        }
      }
      // The dogleg jogs off the LAST line's (synthesized) last vertex —
      // `textLocation` when that line has a single vertex (Codex #467 P2).
      const doglegEnd = multileaderDoglegEnd(e, node, multileaderNodeLastVertex(e, node));
      if (doglegEnd) {
        out.push({
          id: `mleader-dogleg-${ri}-${ni}`,
          kind: "mleader-dogleg",
          x: doglegEnd.x,
          y: doglegEnd.y,
        });
      }
    }
  }
  out.push({ id: "text", kind: "text-location", x: e.textLocation.x, y: e.textLocation.y, patch: { field: "textLocation" } });
  return out;
}

/**
 * HATCH grips — one per unique boundary-path vertex, reusing the one-truth
 * `edgeToCurve` (`drawing-model/hatchBoundaryGeometry.ts`) to resolve each
 * edge's start point uniformly for line / arc / ellipticArc edges (a
 * closed loop's consecutive edges share start↔end, so per-edge start
 * points already de-duplicate the loop's vertex set).
 */
function hatchGrips(e: HatchEntity): GripPoint[] {
  const out: GripPoint[] = [];
  for (let pi = 0; pi < e.boundaryPaths.length; pi++) {
    const path = e.boundaryPaths[pi]!;
    for (let ei = 0; ei < path.edges.length; ei++) {
      const curve = edgeToCurve(path.edges[ei]!);
      if (!curve) continue;
      const p = curve.sample(0);
      out.push({ id: `boundary-${pi}-${ei}`, kind: "vertex", x: p.x, y: p.y });
    }
  }
  return out;
}

/**
 * BLOCK (INSERT) grips — the single insertion-point grip. See the module
 * doc for why dynamic-block / MINSERT parameter grips are out of scope
 * here.
 */
/**
 * Heavy `POLYLINE` grips — WITHDRAWN, deliberately empty (Codex PR #785
 * round 1, F1).
 *
 * AutoCAD does show vertex (and, on a 2D polyline, segment-midpoint) grips
 * here, and this function previously emitted them. Nothing could COMMIT a
 * drag on one: a heavy polyline's vertex list has no typed write path by
 * DESIGN — `descriptors/legacyPolylineTableOle.ts` types `Vertices` as
 * `read-only-calc` ("restructuring a heavy polyline's vertices, or a mesh's
 * M×N grid, is PEDIT / grip territory, not a Properties edit"), so the label
 * is excluded from `getTypedExtendedGeometryOwnedLabels("Polyline")` by
 * construction, the geometry reducer's Step 1d has nothing to route, and the
 * grip-drag translator has no patch it could legally emit. A grip that renders
 * and drags but writes nothing is exactly the "partial support" CLAUDE.md
 * ПРИОРИТЕТ #1 forbids — the same reason {@link tableGrips} withholds the
 * column-width / row-height grips, stated in that function's own doc.
 *
 * These grips come back the moment the vertex list gains a typed writer, i.e.
 * with the PEDIT / mesh-editing slice that owns `vertices` — together with the
 * `translateGripDrag` "Polyline" arm that maps `vertex-N` / `seg-mid-N` onto
 * it, and (for a mesh) the `polylineMeshTopology.ts` runs rather than raw
 * record order. Until then a selected POLYLINE shows its selection highlight
 * and its exact pick geometry (`selectLeaf.ts`) and no edit handles, which is
 * honest rather than inert.
 */
function heavyPolylineGrips(): GripPoint[] {
  return [];
}

/**
 * `ACAD_TABLE` grips — the insertion-point grip only.
 *
 * A table IS a block reference in DXF (`100 AcDbBlockReference`), so it gets
 * the same insertion grip {@link blockGrips} gives an INSERT, which is the
 * grip AutoCAD uses to MOVE a table. It COMMITS: the drag translates to the
 * `Position X` / `Position Y` descriptor pair (`state/gripDragTranslate.ts`),
 * which the typed extended-geometry writer owns for this kind and writes to
 * `insertionPoint` — the same field MOVE / COPY already transform.
 *
 * AutoCAD additionally shows column-width, row-height and table-breaking
 * grips. Those grips exist to EDIT the table's cell layout, and cell/row/
 * column editing is deliberately not part of this change (see
 * `tableEntitySchema`'s "Implemented boundary" doc) — emitting grips that
 * drag but cannot commit would be worse than not showing them. They land with
 * the table-editing feature, together with the cell model they mutate.
 */
function tableGrips(e: TableEntity): GripPoint[] {
  const p = e.insertionPoint;
  return [
    {
      id: "insertion",
      kind: "insertion",
      x: p.x,
      y: p.y,
      ...optZ(p.z),
      patch: { field: "insertionPoint" },
    },
  ];
}

/**
 * `OLE2FRAME` grips — the four frame corners, which is exactly what AutoCAD
 * shows on an OLE object (they scale/reposition the frame). The two stored
 * corners are the upper-left (group 10/20) and lower-right (11/21); the other
 * two are their mixed pairings, so all four are real frame corners, not
 * interpolations.
 *
 * `patch` names the field a drag rewrites, so only the two STORED corners can
 * carry one: dragging `upper-right` moves `lowerRight.x` AND `upperLeft.y`,
 * and `lower-left` the other pair, which the single-field `GripPatchTarget`
 * cannot express. Those two are left `patch`-less exactly the way
 * {@link viewportGrips}'s compound corners are — the drag itself is wired
 * per-id in `state/gripDragTranslate.ts`, which emits both real coordinate
 * labels, so a mixed corner is fully committed despite carrying no single
 * field hint (Codex PR #785 round 1, F1).
 */
function oleFrameGrips(e: OleFrameEntity): GripPoint[] {
  const a = e.upperLeft;
  const b = e.lowerRight;
  return [
    { id: "upper-left", kind: "vertex", x: a.x, y: a.y, ...optZ(a.z), patch: { field: "upperLeft" } },
    { id: "upper-right", kind: "vertex", x: b.x, y: a.y, ...optZ(a.z) },
    { id: "lower-right", kind: "vertex", x: b.x, y: b.y, ...optZ(b.z), patch: { field: "lowerRight" } },
    { id: "lower-left", kind: "vertex", x: a.x, y: b.y, ...optZ(b.z) },
  ];
}

function blockGrips(e: BlockInsertEntity): GripPoint[] {
  const p = e.insertionPoint;
  return [{ id: "insertion", kind: "insertion", x: p.x, y: p.y, ...optZ(p.z), patch: { field: "insertionPoint" } }];
}

/**
 * RECTANGULAR ASSOCIATIVE ARRAY grips — the 5 canonical AutoCAD-style
 * MINSERT-family grips (origin / column-count / row-count / column-spacing
 * / row-spacing), ported field-for-field from
 * `gripCalculation.ts::buildRectangularArrayGrips`.
 *
 * `axisAngle`'s unit depends on `e.associative` — see
 * `common.ts::rotateByArrayAxisAngle`'s doc for the full architecture note
 * (Codex #467 round-2 P2 "Preserve degree-based ArrayRect angles"; round-1's
 * fix used `rotateRad` + `radToDeg` unconditionally, correct only for the
 * associative case).
 */
function arrayRectGrips(e: ArrayRectEntity): GripPoint[] {
  const cols = Math.max(1, Math.round(e.columnCount));
  const rows = Math.max(1, Math.round(e.rowCount));
  const base = e.basePoint;
  const colCountPt = rotateByArrayAxisAngle((cols - 1) * e.columnSpacing, 0, e);
  const rowCountPt = rotateByArrayAxisAngle(0, (rows - 1) * e.rowSpacing, e);
  const colSpacingPt = rotateByArrayAxisAngle(e.columnSpacing, 0, e);
  const rowSpacingPt = rotateByArrayAxisAngle(0, e.rowSpacing, e);
  const axisAngleDeg = arrayAxisAngleDeg(e);
  return [
    { id: "origin", kind: "array-origin", x: base.x, y: base.y, patch: { field: "basePoint" } },
    {
      id: "column-count",
      kind: "array-column-count",
      x: base.x + colCountPt.x,
      y: base.y + colCountPt.y,
      rotationDeg: axisAngleDeg,
      patch: { field: "columnCount" },
    },
    {
      id: "row-count",
      kind: "array-row-count",
      x: base.x + rowCountPt.x,
      y: base.y + rowCountPt.y,
      rotationDeg: axisAngleDeg + 90,
      patch: { field: "rowCount" },
    },
    {
      id: "column-spacing",
      kind: "array-column-spacing",
      x: base.x + colSpacingPt.x,
      y: base.y + colSpacingPt.y,
      patch: { field: "columnSpacing" },
    },
    {
      id: "row-spacing",
      kind: "array-row-spacing",
      x: base.x + rowSpacingPt.x,
      y: base.y + rowSpacingPt.y,
      patch: { field: "rowSpacing" },
    },
  ];
}

/**
 * VIEWPORT grips — the 4 corners of the paper-space frame rectangle
 * (AutoCAD shows a full 4-corner rectangle grip set for a VIEWPORT
 * boundary, unlike the legacy 2-opposite-corner `entityBounds.ts`
 * shortcut). Corner drags are compound (they change both `center` and
 * `width`/`height` depending on which corner) — left as `patch: undefined`
 * for the Wave-2 wiring lane to special-case, matching the LINE
 * midpoint grip's same pattern above.
 */
function viewportGrips(e: ViewportEntity): GripPoint[] {
  const halfW = e.width / 2;
  const halfH = e.height / 2;
  const { x: cx, y: cy } = e.center;
  return [
    { id: "corner-bl", kind: "corner", x: cx - halfW, y: cy - halfH },
    { id: "corner-br", kind: "corner", x: cx + halfW, y: cy - halfH },
    { id: "corner-tr", kind: "corner", x: cx + halfW, y: cy + halfH },
    { id: "corner-tl", kind: "corner", x: cx - halfW, y: cy + halfH },
  ];
}

/**
 * Typed grip points of an entity, read directly from the canonical
 * `DrawingEntity`.
 */
export function getEntityGrips(entity: DrawingEntity): GripPoint[] {
  switch (entity.type) {
    case "Line":
      return lineGrips(entity);
    case "Point":
      return pointGrips(entity);
    case "Circle":
      return circleGrips(entity);
    case "Arc":
      return arcGrips(entity);
    case "Ellipse":
      return ellipseGrips(entity);
    case "Spline":
      return splineGrips(entity);
    case "LWPOLYLINE":
      return lwpolylineGrips(entity);
    case "Text":
      return textGrips(entity);
    case "MText":
      return mtextGrips(entity);
    case "Dimension":
      return dimensionGrips(entity);
    case "Multileader":
      return multileaderGrips(entity);
    case "Revision Cloud":
      return revisionCloudGrips(entity);
    case "Wipeout":
      return wipeoutGrips(entity);
    case "Hatch":
      return hatchGrips(entity);
    case "Block":
      return blockGrips(entity);
    case "Rectangular Associative Array":
      return arrayRectGrips(entity);
    case "Viewport":
      return viewportGrips(entity);
    case "Polyline":
      return heavyPolylineGrips();
    case "Table":
      return tableGrips(entity);
    case "OLE Frame":
      return oleFrameGrips(entity);
    default: {
      const _never: never = entity;
      void _never;
      return [];
    }
  }
}
